import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './Certificate.css';

export default function Certificate({ certificate, onClose }) {
  const printRef = useRef();
  if (!certificate) return null;

  const date = new Date(certificate.completedAt || certificate.issuedAt || Date.now())
    .toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });

  const grade = certificate.score >= 90 ? 'ممتاز مع مرتبة الشرف 🥇'
              : certificate.score >= 80 ? 'جيد جداً مرتفع 🌟'
              : certificate.score >= 70 ? 'جيد جداً 👍'
              : certificate.score >= 60 ? 'جيد ✅'
              : 'ناجح 🏅';

  const handlePrint = () => {
    const style = document.createElement('style');
    style.id = '__cert_print_style__';
    style.textContent = `
      @media print {
        body > * { display: none !important; }
        body > #__cert_print_root__ { display: block !important; }
      }
    `;
    document.head.appendChild(style);

    const root = document.createElement('div');
    root.id = '__cert_print_root__';
    root.style.cssText = 'position:fixed;inset:0;z-index:99999;background:#fff;display:flex;align-items:center;justify-content:center;';
    root.innerHTML = printRef.current.outerHTML;
    document.body.appendChild(root);

    window.print();

    setTimeout(() => {
      document.body.removeChild(root);
      document.head.removeChild(style);
    }, 1000);
  };

  return (
    <div className={`cert-overlay ${isPremium ? 'is-premium' : ''}`} onClick={e => e.target === e.currentTarget && onClose()} style={{ direction: 'rtl' }}>
      <div className="cert-wrapper">
        {/* Action bar — hidden on print */}
        <div className="cert-actions no-print">
          <button className="cert-btn-print" onClick={handlePrint}>🖨️ طباعة / حفظ PDF</button>
          <button className="cert-btn-close" onClick={onClose}>✕ إغلاق</button>
        </div>

        {/* ── Certificate Paper ── */}
        <div className="cert-paper" ref={printRef}>

          {/* Corner book icons */}
          <span className="cert-corner-icon tl">📚</span>
          <span className="cert-corner-icon tr">📚</span>
          <span className="cert-corner-icon bl">📚</span>
          <span className="cert-corner-icon br">📚</span>

          {/* Outer border */}
          <div className="cert-border-outer" />
          <div className="cert-border-inner" />

          {/* ── HEADER ── */}
          <div className="cert-header">
            <div className="cert-logo-row">
              <img src="/logo1.png" alt="Madrasak" className="cert-logo" onError={e => e.target.style.display='none'} />
              <div className="cert-brand">
                <span className="cert-brand-ar">مَدْرَسَك</span>
                <span className="cert-brand-en">MADRASAK · LEARNING PLATFORM</span>
              </div>
            </div>
            <h1 className="cert-title">شَهادة إِتمام كورس مَدْرَسَك</h1>
          </div>

          {/* ── BODY ── */}
          <div className="cert-body">

            {/* Student column */}
            <div className="cert-person-col">
              <div className="cert-photo-frame">
                {certificate.studentAvatar
                  ? <img src={certificate.studentAvatar} alt={certificate.studentName} className="cert-photo" />
                  : <div className="cert-photo-ph">{certificate.studentName?.[0] || '؟'}</div>}
              </div>
              <p className="cert-person-label">الطالب/ة:</p>
              <p className="cert-person-name">{certificate.studentName}</p>
              <p className="cert-person-sub">صورة الطالب</p>
            </div>

            {/* Center content */}
            <div className="cert-center-col">
              <div className="cert-congrats">
                <span className="cert-congrats-line1">ألف مبروك!  </span>
                <span className="cert-congrats-line2">يا بطل خلّصت الكورس! 🏆</span>
              </div>

              <div className="cert-course-info">
                <p className="cert-course-label">علشان خلّصت بنجاح وتفوق كورس:</p>
                <h2 className="cert-course-name">{certificate.courseTitle}</h2>
                <div className="cert-grade">بتقدير: <strong>{grade}</strong></div>
              </div>

              <p className="cert-body-text" style={{ marginTop: 18 }}>مع أستاذنا الكبير:</p>
              <p className="cert-teacher-name">{certificate.teacherName}</p>

              <p className="cert-date-text">وده كان بتاريخ: {date}</p>

              {/* Signatures */}
              <div className="cert-sigs">
                <div className="cert-sig">
                  <div className="cert-sig-line" />
                  <p className="cert-sig-title">توقيع المعلم</p>
                  <p className="cert-sig-name">{certificate.teacherName}</p>
                </div>
                <div className="cert-sig">
                  <div className="cert-sig-line" />
                  <p className="cert-sig-title">إدارة مَدْرَسَك</p>
                  <p className="cert-sig-name">م. محمد رضا</p>
                </div>
              </div>
            </div>

            {/* Teacher column */}
            <div className="cert-person-col">
              <div className="cert-photo-frame">
                {certificate.teacherAvatar
                  ? <img src={certificate.teacherAvatar} alt={certificate.teacherName} className="cert-photo" />
                  : <div className="cert-photo-ph">{certificate.teacherName?.[0] || 'م'}</div>}
              </div>
              <p className="cert-person-label">الأستاذ/ة:</p>
              <p className="cert-person-name">{certificate.teacherName}</p>
              <p className="cert-person-sub">صورة الأستاذ</p>
            </div>
          </div>

          {/* ── FOOTER ── */}
          <div className="cert-footer">
            {/* QR Verification */}
            {certificate.id && (
              <div className="cert-qr-wrap">
                <QRCodeSVG
                  value={`${window.location.origin}/verify/${certificate.id}`}
                  size={64}
                  level="M"
                  includeMargin={false}
                />
                <div className="cert-qr-text">امسح للتحقق</div>
              </div>
            )}

            {/* Seal */}
            <div className="cert-seal">
              <div className="cert-seal-inner">
                <img src="/logo1.png" alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} onError={e => e.target.style.display='none'} />
                <span className="cert-seal-text">توثيق رسمي</span>
                <span className="cert-seal-sub">مدرسك المعتمد</span>
              </div>
            </div>

            {/* Cert ID */}
            {certificate.id && (
              <div className="cert-id-wrap">
                <p className="cert-id">CERT ID: {certificate.id.slice(0, 16).toUpperCase()}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  }
